import { defineStore } from "pinia";
import { ref } from "vue";
import hoverService from "@/services/hoverService";
import customerService from "@/services/customerService";
import { useMeasurementStore } from "@/stores/measurementStore";
import { BlockBlobClient, AnonymousCredential } from "@azure/storage-blob";

export const useHoverStore = defineStore("hover", () => {
  const isHoverConfigured = ref(false);
  const loading = ref(false);
  const error = ref(null);

  const jobs = ref([]);
  const searchTerm = ref("");
  const page = ref(1);
  const pageSize = ref(20);
  const totalRecords = ref(0);

  const connectToHover = async () => {
    loading.value = true;
    error.value = null;

    try {
      const payload = { SystemIdentifier: "Hover", UserSpecific: true };
      const res =
        await customerService.getSystemIntegrationHasIntegrationConfigured(
          payload
        );

      if (res.success) {
        isHoverConfigured.value = res.data;
        if(isHoverConfigured.value) {
          await searchReports();
        }
      } else {
        isHoverConfigured.value = false;
      }
    } catch (err) {
      error.value = err.message;
    } finally {
      loading.value = false;
    }
  };

  const searchReports = async (newSearchTerm = null) => {
    loading.value = true;
    error.value = null;

    if (newSearchTerm !== null) {
      searchTerm.value = newSearchTerm === "All" ? "" : newSearchTerm;
    }

    try {
      const payload = {
        sort_by: "created_at",
        sort_order: "DESC",
        page: 1,
        per: 3,
        deliverable_id: ["2", "3"],
        states: ["complete"],
        search: searchTerm.value,
      };

      const response = await hoverService.getJobs(payload);
      if (response.success) {
        const results = response.data;
        jobs.value = results;
      }
    } catch (err) {
      error.value = err.message;
    } finally {
      loading.value = false;
    }
  };

  // Fetch reports for integrations page
  const fetchReportsForIntegration = async (newSearchTerm = null) => {
    loading.value = true;
    error.value = null;

    if (newSearchTerm !== null) {
      searchTerm.value = newSearchTerm === "All" ? "" : newSearchTerm;
      page.value = 1;
    }

    try {
      const payload = {
        page: page.value,
        per: pageSize.value,
        search: searchTerm.value,
        sort_by: "created_at",
        sort_order: "DESC",
      };

      const response = await hoverService.getJobs(payload);
      if (response.success) {
        const results = response.data;
        jobs.value = results;
        totalRecords.value = results?.length || 0;
      }
    } catch (err) {
      error.value = err.message;
    } finally {
      loading.value = false;
    }
  };

  // Get single report detail
  const getReportDetails = async (jobId) => {
    try {
      const response = await hoverService.getRoofMeasurements(jobId);
      if (response.success && response.data) {
        return response.data;
      }
      return null;

    } catch (err) {
      error.value = err.message
    }
  };

  const importReport = async (jobId) => {
    const measurementStore = useMeasurementStore();

    const job = jobs.value.find((j) => j.id === jobId);

    if (!job) return false;

    loading.value = true;

    try {
      const report = await getReportDetails(jobId);
      if (!report) return false;

      const measurementData = {
        TotalArea: report.TotalArea ? Number(report.TotalArea) : 0,
        Ridges: report.Ridges ? Number(report.Ridges) : 0,
        Valleys: report.Valleys ? Number(report.Valleys) : 0,
        Eaves: report.Eaves ? Number(report.Eaves) : 0,
        Rakes: report.Rakes ? Number(report.Rakes) : 0,
        Hips: report.Hips ? Number(report.Hips) : 0,
      };

      const metaData = {
        provider: "Hover",
        id: job.id,
        link: `/Hover/PDF/${job.id}`,
      };

      measurementStore.setImportedMeasurementData(measurementData, metaData);
      return true;

    } catch (err) {
      error.value = err.message;
      return false;
    } finally {
      loading.value = false;
    }
  };

  const goToPage = (newPage) => {
    page.value = newPage;
    searchReports();
  };

  const clearReports = () => {
    jobs.value = [];
    searchTerm.value = "";
    page.value = 1;
    totalRecords.value = 0;
  };

  const blueprintUpload = async (jobData) => {
    loading.value = true;
    error.value = null;

    try {
      const { deliverableType, jobName, attachments } = jobData;

      if (!attachments || attachments.length === 0) {
        error.value = "No files to upload";
        return { success: false, error: "No files to upload" };
      }

      const tokenResponse = await hoverService.getSasTokens(attachments.length);
      if (!tokenResponse.success) {
        error.value = "Unable to initiate file upload. Please try again.";
        return { success: false, error: error.value };
      }

      const tokens = tokenResponse.data;

      const uploadedFiles = [];
      
      for (let i = 0; i < attachments.length; i++) {
        const file = attachments[i];
        const token = tokens[i];

        try {
          const blockBlobClient = new BlockBlobClient(
            token.Url,
            new AnonymousCredential()
          );

          await blockBlobClient.uploadData(file, {
            blobHTTPHeaders: {
              blobContentType: file.type,
              blobContentDisposition: `attachment; filename="${file.name}"`
            }
          });

          uploadedFiles.push({
            FileAttachmentProvider: "Azure Blob Storage",
            FileAttachmentName: file.name,
            FileAttachmentUrl: token.Url
          });
        } catch (uploadError) {
          console.error(`Failed to upload file ${file.name}:`, uploadError);
          error.value = `Failed to upload file "${file.name}". Please try again.`;
          return { success: false, error: error.value };
        }
      }

      const payload = {
        DeliverableId: Number(deliverableType),
        Name: jobName,
        FileAttachments: uploadedFiles
      };

      const response = await hoverService.blueprintUpload(payload);

      if (response.success) {
        return { success: true };
      } else {
        error.value = response.error || "Failed to send Blueprint";
        return { success: false, error: error.value };
      }
    } catch (err) {
      console.error("Blueprint upload error:", err);
      error.value = err.message || "Error while trying to send Blueprint";
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  };

  const inviteToCapture = async (payload) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await hoverService.inviteToCapture(payload);
      
      if (response.success) {
        return { success: true, data: response.data };
      } else {
        error.value = response.error || "Failed to send invite";
        return { success: false, error: error.value };
      }
    } catch (err) {
      console.error("Invite to Capture error:", err);
      error.value = err.message || "Error while trying to send invite";
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  };

  return {
    isHoverConfigured,
    loading,
    error,
    searchTerm,
    jobs,
    page,
    pageSize,
    totalRecords,

    connectToHover,
    searchReports,
    fetchReportsForIntegration,
    importReport,
    goToPage,
    clearReports,
    blueprintUpload,
    inviteToCapture,
  };
});
